import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ShieldCheck, ArrowLeft, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import OrganiserApplyForm from './OrganiserApplyForm';
import { getOrganiserMe } from '../apis/ride-24';

const ApplyOrganiserPage = ({ user, onUserRefresh }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(user.organiser_status || 'not_applied');
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  const alreadyOrganiser = user.is_verified || user.organiser_status === 'approved';

  const fetchStatus = async () => {
    try {
      const { data } = await getOrganiserMe();
      setStatus(data.organiser_status || data.status);
      setApplication(data.application);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Button
          onClick={() => navigate('/ride-24')}
          variant="ghost"
          className="mb-6 -ml-2 text-gray-300 hover:text-white hover:bg-white/5"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-red-500 font-heading mb-2">
            Verified organiser
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white leading-none">
            Be a verified Organiser
          </h1>
          <p className="text-zinc-400 mt-3">
            Riders trust verified organisers. Submit your details for admin review.
          </p>
        </div>

        {loading ? (
          <Card className="bg-zinc-950 border border-zinc-900 text-gray-200 h-40 animate-pulse" />
        ) : alreadyOrganiser || status === 'approved' ? (
          <StatusCard
            icon={<CheckCircle2 className="w-8 h-8 text-red-500" />}
            title="You're a verified Organiser"
            body="Head to Create Ride and organise your first ride."
            action={
              <Button
                onClick={() => navigate('/ride-24/create-event')}
                className="bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-widest"
                data-testid="apply-success-cta"
              >
                Create a ride
              </Button>
            }
            testId="status-approved"
          />
        ) : status === 'pending' ? (
          <StatusCard
            icon={<Clock className="w-8 h-8 text-yellow-500" />}
            title="Application under review"
            body={
              application
                ? `Submitted on ${new Date(application.submitted_at).toLocaleDateString()}. Our admin will get back to you soon.`
                : 'Our admin will review your application shortly.'
            }
            testId="status-pending"
          />
        ) : (
          <>
            {status === 'rejected' && (
              <div className="mb-6 rounded-sm bg-red-950/30 border border-red-900/40 p-3 flex gap-3">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-200">
                  Your previous application was rejected
                  {application?.admin_notes ? `: "${application.admin_notes}"` : '.'} You can submit again.
                </div>
              </div>
            )}
            <Card className="bg-zinc-950 border border-zinc-900 text-gray-200">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-red-500" />
                  Organiser application
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Upload a photo, ID document, and enter your ID details.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <OrganiserApplyForm
                  defaultName={user.name}
                  onSubmitted={async () => {
                    await fetchStatus();
                    if (onUserRefresh) onUserRefresh();
                  }}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

const StatusCard = ({ icon, title, body, action, testId }) => (
  <Card className="bg-zinc-950 border border-zinc-900 text-gray-200" data-testid={testId}>
    <CardContent className="py-10 text-center">
      <div className="flex justify-center mb-3">{icon}</div>
      <h2 className="font-heading text-2xl uppercase tracking-tight text-white mb-2">{title}</h2>
      <p className="text-zinc-400 mb-4">{body}</p>
      {action}
    </CardContent>
  </Card>
);

export default ApplyOrganiserPage;
